// actions/territories/thunks.ts
import settingsApi from '@/apis/settingsApi';
import { setRequest, setTerritories, setTerritoryColors,setSelectedTerritory } from './pure';
import { SET_TERRITORY_REQUEST , SET_SELECTED_TERRITORY } from '@/actions/types';
import { indexBy, sortBy } from 'underscore';
import { getNcolorsBasti } from '@/components/Common/colorGen';
import { AppDispatch, RootState } from '@/index';
import { Territory, TerritoryListResponse } from '@/config/types/Territories';
import { toastr } from 'react-redux-toastr';


type TerritoryDetailResponse = {
  results: Territory[];
  not_found: string[];
};

export function getTerritories(changePage?: boolean, pageSize?: number) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<TerritoryListResponse | void> => {
    const { territory_request, territories } = getState().territories;

    const nextPage = changePage ? (territory_request.page ?? 1) + 1 : 1;
    const params = { page: nextPage, page_size: pageSize };

    try {
      const { data } = await settingsApi.getTerritories(params);
      const incoming = Array.isArray(data?.results) ? data.results : [];

      const mergedRaw: Territory[] = nextPage > 1 ? [...(territories ?? []), ...incoming] : incoming;

      // ✅ dedupe by UUID id
      const merged: Territory[] = Array.from(
        new Map(mergedRaw.map((t: Territory) => [t.id, t])).values()
      );

      // const colors = getNcolorsBasti(merged.length);
      // const assignedColors = indexBy(
      //   merged.map((t: Territory, i: number) => ({ color: colors[i], id: t.id })),
      //   'id'
      // );
      const assignedColors = indexBy(
        merged.map((t: Territory) => ({ color: colorFromId(t.id), id: t.id })),
        'id'
);


      dispatch(setTerritoryColors(assignedColors));
      dispatch(setTerritories(sortBy(merged, 'name')));

      dispatch(
        setRequest(
          {
            page: nextPage,
            total_pages: data.total_pages,
            num_results: data.count,
            results_per_page: pageSize,
          },
          SET_TERRITORY_REQUEST
        )
      );

      return data;
    } catch (err) {
      console.error('getTerritories failed', err);
    }
  };
}

function extractTerritory(data: any): Territory | undefined {
  // Case A: wrapped response { results: [...] }
  if (data && Array.isArray(data.results)) return data.results[0];

  // Case B: direct object { id, name, postal_codes, bounds }
  if (data && typeof data === 'object' && data.id) return data as Territory;

  return undefined;
}

export function getTerritoryById(id: string) {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { data } = await settingsApi.getTerritory(id);

      const territory = extractTerritory(data);
      if (!territory) {
        console.warn('Territory not found / unexpected response shape:', { id, data });
        return;
      }

      dispatch(setSelectedTerritory(territory));

      // update list item with fetched territory
      const current = getState().territories.territories ?? [];
      const next = current.map((t: Territory) => (t.id === territory.id ? territory : t));
      dispatch(setTerritories(next));

      return territory;
    } catch (err) {
      console.error('getTerritoryById failed', err);
    }
  };
}

export type ReassignPostalCodeArgs = {
  postalCodeId: string;
  fromTerritoryId: string;
  toTerritoryId: string;
};

/**
 * Reassign a postal code from one territory to another.
 * Backend requires two calls:
 *  1) DELETE postal code from current territory
 *  2) POST postal code to target territory
 */
export function reassignPostalCode({ postalCodeId, fromTerritoryId, toTerritoryId }: ReassignPostalCodeArgs) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    // 1) remove from current
    await settingsApi.assignZip('delete', [postalCodeId], fromTerritoryId);

    // 2) add to target
    await settingsApi.assignZip('post', [postalCodeId], toTerritoryId);

    // 3) refresh both territories in store (list + selected)
    // getTerritoryById updates selected_territory (if it matches) and updates the list item.
    await dispatch(getTerritoryById(fromTerritoryId) as any);
    if (toTerritoryId !== fromTerritoryId) {
      await dispatch(getTerritoryById(toTerritoryId) as any);
    }

    // If the user currently has one of these selected, ensure we keep the selection consistent.
    const selectedId = getState().territories.selected_territory?.id;
    if (selectedId && (selectedId === fromTerritoryId || selectedId === toTerritoryId)) {
      await dispatch(getTerritoryById(selectedId) as any);
    }
  };
}


export const deleteTerritories =
  (ids: string[]) =>
  async (dispatch: any, getState: () => RootState) => {
    if (!ids?.length) return;

    // if current selected territory is deleted, clear it
    const { territories } = getState();
    const selectedId = territories?.selected_territory?.id;
    if (selectedId && ids.includes(selectedId)) {
      dispatch(setSelectedTerritory(undefined as any));
    }

    // backend deletes
    await Promise.all(ids.map((id) => settingsApi.deleteTerritory(id)));

    return true;
  };

  export const deletePostalCodesFromTerritory =
  (territoryId: string, postalCodeIds: string[]) =>
  async (dispatch: any) => {
    if (!territoryId || !postalCodeIds?.length) return;

    await settingsApi.assignZip('delete', postalCodeIds, territoryId);

    // Refresh the selected territory so UI updates (postal_codes list shrinks)
    await dispatch(getTerritoryById(territoryId) as any);

    return true;
  };

  export const addPostalCodesToTerritory =
  (territoryId: string, postalCodes: string[]) =>
  async (dispatch: any) => {
    if (!territoryId || !postalCodes?.length) return;

    await settingsApi.assignZip('post', postalCodes, territoryId);

    await dispatch(getTerritoryById(territoryId) as any);
    return true;
  };

function colorFromId(id: string): string {
  // Simple hash → hue. Stable per ID, looks different across IDs.
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;

  // HSL → CSS string Leaflet accepts
  return `hsl(${hue}, 70%, 50%)`;
}

export const createTerritoryFromPostalCodes =
  (name: string, postalCodes: { country: string; code: string }[]) =>
    async (dispatch: any) => {
      try {
        // 1. lookup postal codes
        const lookupRes = await settingsApi.getPostalCodesByInput({
          postal_codes: postalCodes,
        });

        const ids = lookupRes.data?.results?.map((r: any) => r.id) ?? [];

        if (!ids.length) {
          throw new Error('No postal codes found');
        }

        // 2. create territory
        const createRes = await settingsApi.createTerritory({
          name,
          postal_code_ids: ids,
        });

        return createRes.data;
      } catch (err) {
        return Promise.reject(err);
      }
    };


export const addPostalCodesToTerritoryFromInput =
  (territoryId: string, postalCodes: { country: string; code: string }[]) =>
    async (dispatch: any) => {
      try {
        // 1) lookup -> ids
        const lookupRes = await settingsApi.getPostalCodesByInput({
          postal_codes: postalCodes,
        });

        const ids: string[] = (lookupRes.data?.results ?? [])
          .map((r: any) => r?.id)
          .filter(Boolean);

        if (!ids.length) throw new Error('No postal codes found');

        // 2) assign by ids (body: string[])
        const assignRes = await settingsApi.assignPostalCodeIds(territoryId, ids);

        return assignRes.data;
      } catch (err) {
        return Promise.reject(err);
      }
    };

export const renameTerritoryName =
  (territoryId: string, newName: string) =>
    async (dispatch: any) => {
      try {
        await settingsApi.renameTerritoryName(territoryId, newName);

        // refresh both list + selected detail
        await dispatch(getTerritories(false, 100) as any);
        await dispatch(getTerritoryById(territoryId) as any);

        return true;
      } catch (err) {
        return Promise.reject(err);
      }
    };

